/**
 * Gemeinsamer Mikrofon-Pegel-Visualizer (Frage-Sheet + Composer-Diktat).
 * Rein visuell — sichtbarer Beweis, dass die Aufnahme laeuft. KEINE
 * Automatik daran haengen (Auto-Stopp bleibt abgeschafft, 2026-07-20).
 */
import { wacheRechentakt, type TaktEreignis, type TaktWache } from "./rechentakt.js";
import {
  anzeigeWertAusDb,
  anzeigeWertAusQuadratmittel,
  anzeigeWertNativ,
  neueAnpassung,
  kennlinie as kennlinieAus,
  neuePegelAnzeige,
  neuerNachgereichterPegel,
  ZEICHNE_AB,
  type PegelAnzeige,
  type PegelLage,
} from "./pegel-anzeige.js";

export interface MicViz {
  el: HTMLElement;
  /**
   * 2026-08-01 (Ticket 1536): Den AudioContext SCHON IN DER FINGERGESTE
   * anlegen und wecken — noch bevor die Tonspur existiert.
   *
   * Warum: `start()` laeuft erst, wenn das Mikro offen ist, also mehrere
   * Frames nach dem Druck. Ein dort neu angelegter AudioContext startet auf
   * iOS ANGEHALTEN, und `resume()` ausserhalb einer Geste greift nicht
   * zuverlaessig. Der Analyser liefert dann Nullen: Balken tot, Aufnahme
   * heil — genau Martins Beobachtung. Dass es „haeufig" und nicht immer
   * auftrat, passt dazu: hatte die Seite in dieser Sitzung schon einen
   * wachen Kontext, gelingt das Wecken oft doch.
   *
   * `unlockAudio()` half hier nicht — das weckt den GETEILTEN Klang-Kontext,
   * nicht den, den der Visualizer sich spaeter selbst baut.
   */
  prepare(): void;
  start(stream: MediaStream | MediaStreamTrack): void;
  stop(): void;
  /**
   * 2026-07-25: Von aussen gefuetterter Pegel (0..1) — fuer Quellen, die
   * bereits einen eigenen AudioContext auf dem Mikro haben (Soniox-Diktat).
   * Ein ZWEITER Kontext auf derselben Quelle brachte in WKWebView das Diktat
   * zum Schweigen; deshalb messen wir dort nicht selbst, sondern zeigen nur.
   */
  zeigeOhneEigeneMessung(): void;
  /**
   * pre.3623: `null` = die Quelle konnte nicht antworten (Brücke gescheitert).
   * Die Stille-Regel zählt das wie bisher als 0; die Anzeige zählt es nicht
   * als Signal (nach BLIND_AB_MS „Pegel nicht messbar“).
   */
  pushLevel(level: number | null): void;
  /**
   * 08.10.2026: Fensterpegel (dBFS, je 100 ms, in Aufnahme-Reihenfolge), die der Server beim Nachhören aus der
   * Aufnahme selbst gemessen hat. Gezeigt NUR, solange die eigene Messung nichts liefert (Rechentakt steht) — als
   * „Pegel verzögert“. Die Stille-Entscheidung bekommt sie nie (pegel-anzeige.ts, `neuerNachgereichterPegel`).
   */
  nachgereicht(db: readonly number[]): void;
  /**
   * Ton zählt erst ab diesem Zeitpunkt (performance.now) — davor gilt jedes
   * Bild als leise. Für den eigenen Ton beim Öffnen des Nachlausch-Fensters
   * (ptt-modal.ts, TON_SCHONFRIST_MS): er ist keine Sprache, und ein Satz,
   * der in der Schonfrist beginnt, zählt ab ihrem Ende. (Tickets 2668/2697)
   */
  tonZaehltAb(t: number): void;
  /**
   * Der Grundpegel lernt erst ab diesem Zeitpunkt (performance.now). Für das
   * Nachlausch-Fenster (ptt-modal.ts, NACHLAUSCH_GRUND_AB_MS): die ersten
   * Bilder eines frisch geöffneten Mikros liegen unter dem Rauschen des
   * Raums, und als Grund machten sie das Rauschen selbst zu Ton. (Ticket 2668)
   */
  grundLerntAb(t: number): void;
  /**
   * Lag in der Schonfrist (tonZaehltAb) anhaltender Ton — nach derselben
   * Regel wie danach? Dann war es der eigene Ton ODER ein kurzes Wort; am
   * Pegel ist das nicht zu trennen. Der Aufrufer darf die Aufnahme dann
   * nicht verwerfen. Gilt je Schonfrist, neu ab jedem tonZaehltAb.
   * (29.09.2026, Ticket 2668, Nachbesserung)
   */
  tonInSchonfrist(): boolean;
  /**
   * Die Stille des letzten Bildes nach der ALTEN Grenze (Grundpegel, ohne Sprachgrenze)
   * — nur gemessen, entscheidet nichts. Gleich der gemeldeten Stille, solange die
   * Sprachgrenze nicht galt. Für `[ende-token] altMs=` (07.10.2026).
   */
  stilleAltMs(): number;
}

/** Ab wann gilt „es kommt nichts an" — kurz genug zum Merken, lang genug
 *  fuer eine Sprechpause. */
const STILL_SCHWELLE_MS = 2500;

/**
 * Über diesem Pegel (0…1) gilt ein Bild als „laut", darunter als Stille —
 * das Mindeste; die Grenze folgt dem Grundrauschen (stilleGrenze).
 *
 * 23.09.2026, Tickets 2512/2420: als Name, damit die Messzeile `[ptt-stille]`
 * die Grenze nennt, die hier WIRKLICH gilt, statt eine Kopie der Zahl.
 * ⚠️ Die zwei Wege haben verschiedene Kennlinien: Browser `(rms·14)^0,6`
 *    (0,03 ≙ rund −74 dBFS Effektivwert), nativ `(dB+50)/50` aus
 *    `averagePower` (0,03 ≙ −48,5 dBFS).
 */
export const PEGEL_GRENZE = 0.03;

/** Was die Anzeige vorliest, je Lage (pegel-anzeige.ts). */
const LAGE_NAME: Record<PegelLage, string> = {
  misst: "Pegel der Aufnahme",
  nachgereicht: "Pegel aus der Aufnahme, einige Sekunden verzögert – die Anzeige selbst misst gerade nicht",
  blind: "Pegel nicht messbar – es kommt kein Signal an der Anzeige an",
};
/** Was sichtbar dasteht, wenn kein Signal ankommt. */
const LAGE_TEXT_BLIND = "Pegel nicht messbar";
/**
 * Nachgereichter Pegel: kein sichtbarer Text („Pegel verzögert“ verstand Martin nicht, 17.10.2026) —
 * die Balken zeigen ihn gedämpft, der Screenreader-Name (LAGE_NAME) bleibt.
 */
const LAGE_TEXT: Record<PegelLage, string> = { misst: "", nachgereicht: "", blind: LAGE_TEXT_BLIND };

/**
 * Die Grenze folgt dem Grundrauschen. (25.09.2026, Ticket 2606)
 *
 * Martin: *„Da steht, dass nach zwei Sekunden Stille abgesendet wird —
 * tatsächlich passiert das nicht, ich muss ‚senden' sagen."* Die Messzeile
 * `[ptt-stille]` (seit 23.09.) hat es entschieden: im Auto lag schon der
 * LEISESTE Zehntel des Pegels bei 0,07–0,33 (Motor, Fahrtwind), die feste
 * Grenze bei 0,03. Unter 0,03 kam es nie, also galt nie etwas als Stille
 * — `laengste=0ms` bei `gesprochen=ja`. Am Schreibtisch (p10 0,00–0,01)
 * endeten dieselben Aufnahmen durch Stille.
 *
 * Jetzt: das untere Zehntel der letzten sechs Sekunden ist der Grundpegel;
 * Stille ist, was nicht deutlich darüber liegt (Faktor 2). Nie unter der
 * alten festen Grenze — im stillen Raum ändert sich nichts.
 */
const GRUNDPEGEL_FENSTER_MS = 6_000;
const GRUNDPEGEL_FAKTOR = 2;
/** Erst ab so vielen Bildern im Fenster zählt der Grundpegel. */
const GRUNDPEGEL_MIN_BILDER = 8;
/**
 * So lange läuft eine Aufnahme an; diese Bilder zählen nicht zum
 * Grundpegel. (27.09.2026, Ticket 2697, zweite Nachprüfung) Die ersten zwei
 * bis vier Bilder steigen erst an (Fahrt vom 27.09.: 0,04–0,13, dann
 * Fahrgeräusch um 0,3), und das untere Zehntel weniger Bilder ist das
 * leiseste. Mit ihnen als Grund wurde Fahrgeräusch hinter dem Hochpass in
 * fünf von neun Aufnahmen nach 0,13–0,16 s Ton, also „gesprochen“, lange vor
 * dem ersten Wort; ohne sie in keiner der 42 nachgerechneten.
 */
const GRUNDPEGEL_ANLAUF_MS = 100;

export interface Grundpegel {
  merke(jetzt: number, roh: number): void;
  /** Das untere Zehntel des Fensters (ohne den Anlauf), oder null, solange zu wenig da ist. */
  wert(): number | null;
}

export function neuerGrundpegel(): Grundpegel {
  const bilder: { t: number; v: number }[] = [];
  let erstes: number | null = null;
  return {
    merke(jetzt, roh) {
      erstes ??= jetzt;
      if (jetzt - erstes < GRUNDPEGEL_ANLAUF_MS) return;
      bilder.push({ t: jetzt, v: roh });
      while (bilder.length > 0 && jetzt - bilder[0].t > GRUNDPEGEL_FENSTER_MS) bilder.shift();
    },
    wert() {
      if (bilder.length < GRUNDPEGEL_MIN_BILDER) return null;
      const s = bilder.map((b) => b.v).sort((a, b) => a - b);
      return s[Math.floor(0.1 * (s.length - 1))] ?? null;
    },
  };
}

/**
 * Die Grenze zwischen Stille und Ton bei diesem Grundpegel.
 *
 * Ohne Grundpegel gibt es keine Grenze: kein Bild ist laut (Unendlich).
 * (27.09.2026, Ticket 2697, zweite Nachprüfung) Bis pre.3377 galt hier die
 * feste PEGEL_GRENZE, bis acht Bilder da waren — Fahrgeräusch liegt immer
 * darüber. Nativ (ein Bild alle 100 ms) galt es so nach 0,35 s als
 * gesprochen, und die Karte schickte nach 4 s ab, ohne dass ein Wort fiel;
 * im Browser ab 25 ms je Bild genauso. Die Bilder davor entscheidet jetzt
 * der Grundpegel, sobald es ihn gibt (neuerTonlauf).
 */
export function stilleGrenze(grund: number | null): number {
  return grund === null
    ? Number.POSITIVE_INFINITY
    : Math.min(GRENZE_HOECHSTENS, Math.max(PEGEL_GRENZE, grund * GRUNDPEGEL_FAKTOR));
}

/**
 * Die Grenze liegt nie über GRENZE_HOECHSTENS. (20.10.2026, Martin: „das Mikro schaltet ab, während ich noch rede“)
 *
 * Der Pegel endet bei 1,0 (Anschlag der Kennlinie, ab rund −23 dBFS). Der Grundpegel ist das untere Zehntel der
 * letzten 6 s — spricht man ohne Pause und laut, ist auch dieses Zehntel Sprache. Journal 20.10. 08:20 (Konto 1,
 * via=nach-frage, beide Fälle direkt nach der Frage weitergeredet): `pegel_p10/p50/p90=0.533/1.000/1.000 grenze=1.125`
 * und `0.286/1.000/1.000 grenze=1.203` — die Grenze lag ÜBER dem höchsten möglichen Pegel, kein Bild zählte als Ton,
 * die Stille wuchs mitten in der Rede (`laengste=4786ms` bei `dauer=4815ms`) und die Obergrenze schickte ab
 * (`entscheid=obergrenze textRuhtMs=18 weitergesprochen=ja`). Derselbe Fehler machte jedes Wort „im Stillen“
 * (`imstillen=1/1/1/1`), und der Rauschfilter nahm echte Wörter aus Jevs Urteil. Vorher (12.–19.10.) 7-mal.
 *
 * 0,8 lässt den gemessenen Fahrtlärm (Grund bis 0,33, Grenze bis 0,66) unberührt und hält Abstand zum Anschlag:
 * Sprache am Anschlag zählt immer als Ton.
 */
export const GRENZE_HOECHSTENS = 0.8;

/*
 * ── Die Grenze folgt auch der eigenen Sprache (07.10.2026, „Sätze gehen nicht ab“) ──
 *
 * Martin, 07.10.: *„Ich bin mit dem Satz schon 10 mal fertig, und er schickt es
 * einfach nicht los, obwohl es offensichtlich ist.“* Das Journal vom Morgen: nach
 * dem letzten Wort um 06:35:48 stand der Text 5 und dann 9 s still, der Pegel
 * aber wurde alle ~1,4 s zurückgesetzt (`[pause-weiter] stillMs=1400 / 1367`), und
 * die Auswertung der Datei am Server sagte `endstille=16.0` — die ganze Zeit war es
 * still. Die Mindestgrenze `PEGEL_GRENZE` 0,03 liegt in der Kennlinie (rms·14)^0,6
 * bei rund −74 dBFS, Martins Sprache bei rund −24 dBFS: jedes Atmen, Klicken oder
 * Rascheln (3–20 % der Sprechlautstärke) zählte als Sprechen und setzte die Stille
 * zurück. Das Grundrauschen (Ticket 2606) hilft hier nicht: sein Raum ist leise,
 * die Grenze bleibt auf dem Mindestwert.
 *
 * Darum: Ton zählt nur, wenn er mindestens SPRACH_ABSTAND der Sprache DIESER Aufnahme
 * hat (das obere Zehntel der lauten Bilder, `neuerSprachpegel`) — aber NUR, wenn die
 * Mitschrift seit TEXTRUHE_AB_MS (2 s, shared/stille-auto.ts) unverändert steht. Derselbe
 * Gedanke wie „Laut ohne neues Wort“ dort: ein Laut, aus dem kein Wort wird, setzt die
 * Stille nicht zurück. Erst ab SPRACHPEGEL_MIN_BILDER lauten Bildern gibt es einen
 * Sprachpegel; davor, und solange der Text sich bewegt, gilt die alte Grenze.
 *
 * Warum das Tor am Text (Prüfbefund 07.10., Fall 05.10. 08:48:22): die Pause ab Sekunde
 * 47,8 endete nach dem alten Weg bei 2278 ms (`[pause-weiter] stillMs=2267`) durch einen
 * LEISEN ANSATZ vor dem Weitersprechen (roh 0,036–0,16, −65…−49 dBFS, für WebRTC-VAD
 * 1,1 s lang Rede). Allein mit der Sprachgrenze lief die Stille bis 4352 ms weiter, Jev
 * sagte bei 2,39 s mit 0,86 und „.“ „senden“ — die Karte wäre bei Sekunde 50,7
 * abgegangen, die Live-Mitschrift änderte sich aber noch bis Sekunde 57,8: rund 7 s
 * Diktat weg. Am Abstand allein ist das nicht zu lösen. Dort war das „.“ erst 0,1–0,4 s
 * vor dem Rücksetzen gekommen (die zwei Fragen an Jev: `stillMs=1167` ohne, `2166` mit
 * Zeichen) — mit dem Tor gilt der alte Weg, die Stille endet wie vorher.
 *
 * Wie viel Abstand (Messung des Prüfers an 168 echten Aufnahmen 04.–07.10., mit dem
 * echten Grundpegel/Tonlauf, WebRTC-VAD als Maßstab für „es wird gesprochen“; ohne das
 * Tor gerechnet, also die obere Schranke der Wirkung): −36 dB wirkt an den Fällen 06:36
 * und 06:38 und am Ende aller Aufnahmen wie −30 dB, verschluckt aber weniger kurze
 * Wörter (Wörter mit < 120 ms über der Grenze 14 statt 62); das „Knie“ für leise
 * Stimmen liegt zwischen −30 und −25 dB.
 *
 * Nachgerechnet mit dem Tor (dieselben 168 Aufnahmen, die echte Anzeige; die Zeiten der
 * Mitschrift modelliert: in jedem Wortlauf alle 0,7 s und 0,7 s nach seinem Ende; der
 * Server sagt immer ja): von 1236 Wortlücken ab 0,6 s MITTEN in den Aufnahmen erreicht
 * die Stille 1,5 s alt in 138, ohne Tor in 144, mit Tor in 139 (2,5 s: 40 / 46 / 41;
 * 3,5 s: 15 / 17 / 15; 6 s: je 7). Am Ende: 6 s Stille im Fall 06:36 bei Sekunde 45,2
 * (alt nie), 06:38 bei 32,8 (alt nie); die längste Zeit vom letzten Wort bis 1,5 s Stille
 * 5,4 s statt 9,7 s.
 * ⚠️ Restrisiko: das Tor öffnet rund 2,7 s nach dem letzten Wort (0,7 s bis zur Mitschrift
 *    plus 2 s). Danach überhört die Stille einen leisen Ansatz unter der Sprachgrenze. Ein
 *    Urteil, das eine NEUE Mitschrift auslöste, kommt immer bei geschlossenem Tor an
 *    (≤ 0,5 s nach der Änderung) — offen bleiben die späteren Stufen (Jev 0,70–0,79 sendet
 *    erst ab 2,5/3,5 s Stille) und die Obergrenze 6 s. Im Modell ist 08:48:22 so eine
 *    Lücke (2,8 s statt 2,3 s Stille), weil es das späte „.“ nicht kennt; mit den echten
 *    Zeiten aus dem Journal blieb das Tor zu (tests/frontend/stille-sprachgrenze.test.ts).
 * ⚠️ Ehrlich: „36 dB unter der Sprache“ stimmt nur, solange das obere Zehntel unter dem
 *    Anschlag 1,0 bleibt. In 67 von 164 Aufnahmen erreichte es ihn irgendwann (in 49
 *    stand es am Ende dort, der Median am Ende liegt bei 0,52) — dann steht die Grenze
 *    FEST bei 0,083 (≈ −59 dBFS), und die echte Sprache liegt mehr als 36 dB darüber.
 *
 * ⚠️ NUR der Browser-Weg (eigene Messung). Der native Pegel hat eine andere Kennlinie
 *    ((dB+50)/50) und kam in den 197 Aufnahmen vom 04.–07.10. nicht vor.
 * ⚠️ NUR im Auto-Modus: die Karte wendet die Sprachgrenze nur an, wenn die letzte Antwort
 *    von POST /api/stille-auto `sprachgrenze: true` sagt (Schalter `STILLE_SPRACHGRENZE=0`
 *    am Server, server/stille-auto-routen.ts). Gefragt wird nur bei „Auto“ — in den festen
 *    Stufen 2/3 s, vor der ersten Antwort und ohne Antwort gilt der alte Weg. Das ist
 *    Absicht: die Änderung bleibt auf Martins Auto-Modus beschränkt.
 * ⚠️ Das Journal (`grenze=`, Wörter „im Stillen“, `rauschBefund`) bleibt bei der alten Grenze —
 *    nur Ton und Stille nehmen die neue; sie steht als `sprachgrenze=` in `[ptt-stille]`,
 *    und `[ende-token]` trägt `altMs=`: die Stille nach der alten Grenze beim Senden.
 *
 * RÜCKDREHEN: Sendungen, die nur die Sprachgrenze ausgelöst hat (`altMs` unter der
 * Stufe, in der gesendet wurde), mit den übrigen vergleichen. Folgt auf sie öfter als 1 von
 * 20 innerhalb von 10 s ein neuer Druck der Sprechtaste (oder öfter als bei den übrigen),
 * wird `STILLE_SPRACHGRENZE=0` gesetzt (Neustart nur über scripts/neustart.sh).
 */
/** Abstand zur Sprache dieser Aufnahme: −36 dB in der Amplitude = 0,0158; in der Kennlinie (rms·14)^0,6 ist das 0,0158^0,6 ≈ 0,083. */
export const SPRACH_ABSTAND = 0.083;
/** Erst ab so vielen lauten Bildern gibt es einen Sprachpegel (rund 0,5 s Sprache bei 17 ms je Bild). */
const SPRACHPEGEL_MIN_BILDER = 30;
/** Fächer des Histogramms: 100 Stufen von 0 bis 1 — genügt für das obere Zehntel, wächst nicht mit der Aufnahme. */
const SPRACHPEGEL_STUFEN = 100;

export interface Sprachpegel {
  /** Ein gemessenes Bild: zählt nur, wenn es über der alten Grenze liegt (also Ton ist). */
  merke(roh: number, grenze: number): void;
  /** Das obere Zehntel der lauten Bilder bisher, oder null, solange zu wenig da ist. */
  wert(): number | null;
}

export function neuerSprachpegel(): Sprachpegel {
  const faecher = new Array<number>(SPRACHPEGEL_STUFEN).fill(0);
  let n = 0;
  return {
    merke(roh, grenze) {
      if (!(roh > grenze) || !Number.isFinite(roh)) return;
      faecher[Math.min(SPRACHPEGEL_STUFEN - 1, Math.floor(roh * SPRACHPEGEL_STUFEN))]++;
      n++;
    },
    wert() {
      if (n < SPRACHPEGEL_MIN_BILDER) return null;
      const ziel = 0.9 * n;
      let summe = 0;
      for (const [i, f] of faecher.entries()) {
        summe += f;
        if (summe >= ziel) return (i + 1) / SPRACHPEGEL_STUFEN;
      }
      return 1;
    },
  };
}

/** Die Untergrenze für Ton aus der Sprache dieser Aufnahme — 0, solange es keinen Sprachpegel gibt. */
export function sprachGrenze(sprache: number | null): number {
  return sprache === null ? 0 : SPRACH_ABSTAND * sprache;
}

/**
 * Anhaltender Ton: so viel lauter Ton muss innerhalb von TON_FENSTER_MS
 * zusammenkommen, bis er zählt — als Sprache (ptt-modal.ts, `gesprochen`)
 * UND als Ende der Stille. Ein einzelnes lautes Bild zählt nie, auch nicht
 * mit. EINE Regel, EINE Zahl. (27.09.2026, Tickets 2697 und 2668)
 *
 * Fall 2697 (09:30:39): 13,5 s echte Stille in der Aufnahme, gemessen
 * höchstens 3,08 s am Stück. Die Grenze folgt dem Grundrauschen (×2), das
 * Rauschen lag immer wieder kurz darüber — jedes solche Bild riss die
 * Stille ab. Fall 2668 (07:58:52): ein lautes Bild hielt das
 * Nachlausch-Fenster offen.
 *
 * ⚠️ pre.3374 (Nachprüfung): in pre.3373 zählten nur 120 ms AM STÜCK.
 *    Sprache im Fahrgeräusch liegt aber nur in kurzen Stücken über der
 *    Grenze, dazwischen Täler. Die Stille wuchs so mitten im Satz weiter:
 *    der Satz dieser Aufnahme (Sekunde 17–31) mit Fahrgeräusch 0,1 / 0,15 /
 *    0,2 zugemischt — längste Stille 2,2 / 3,3 / 4,8 s, jetzt 0,9 / 1,3 /
 *    1,5 s. Jetzt zählen die Stücke im Fenster zusammen. Einzelne Bilder
 *    bleiben draußen: in der Pause des Falls kam das Rauschen in Stücken von
 *    einem bis drei Bildern, und bei Rauschen ist das einzelne Bild der
 *    häufigste Fall, bei Sprache nicht.
 *
 * Gezählt wird je Bild die übliche Bilddauer (`bildDauer`), nicht der
 * zufällige Abstand zum vorigen: nativ kommt der Pegel alle 100 ms, aber
 * 5–60 ms verspätet — folgt eine frühe Antwort auf eine späte, zählte ein
 * lautes Bild mit dem Abstand nur 45 ms. Warum 120 ms (in pre.3371 150 ms
 * als Summe der Abstände):
 *   - Ein Ton von 150 ms trifft im Browser (16–35 ms je Bild, Median 18 ms)
 *     mindestens so viele Bilder, dass 120 ms zusammenkommen; nativ meist
 *     zwei Abfragen = 200 ms. Ein kurzes „Ja“ mit 150 ms über der Grenze zählt.
 *   - Ein Knacks trifft ein bis zwei Bilder: im Browser höchstens 70 ms,
 *     nativ eine Abfrage — die zählt als einzelnes Bild gar nicht. Den
 *     eigenen Ton beim Öffnen hält die Schonfrist fern (ptt-modal.ts,
 *     TON_SCHONFRIST_MS).
 *   ⚠️ Nativ heißt das: zwei laute Abfragen in Folge sind Ton, eine nicht —
 *      feiner trennt ein 100-ms-Takt nicht. Liegt ein Ton von 150 ms ganz
 *      in EINER Abfrage (155 ms Abstand), zählt er nicht.
 *   ⚠️ Zwei Knackse zu je zwei Bildern in TON_FENSTER_MS zählen bei
 *      langsamem Bildschirmtakt (35 ms je Bild, 140 ms) zusammen als Ton.
 *   ⚠️ Die Bildrate entscheidet bei Knacksen mit, und das bleibt so.
 *      (28.09.2026, Ticket 2697, Nachprüfung zu pre.3431) Jedes Bild misst
 *      die letzten 1024 Werte (21 ms). Bei 60 Bildern/s überlappen sich die
 *      Fenster: ein Knacks von 10–20 ms trifft zwei bis drei Bilder und
 *      zählt mit, bei 30 Bildern/s meist nur eines. Im Fall 16:35
 *      (Campingplatz) ging die Pause darum bei 30–34 ms je Bild meist ab,
 *      bei 17–20 ms fast nie (knacks-bildrate-2697.test.ts). Silbenspitzen
 *      im Fahrgeräusch und das Ausklingen eines Wortes sind aber genauso
 *      kurz. Eine Regel, die ein Stück erst ab 35 ms Laut zählt (bei 60
 *      Bildern/s ab vier Bildern), ließ die Stille im Satz bei Fahrgeräusch
 *      0,2 von 1,5 auf 4,8 s wachsen, und die Karte schickte mitten im Satz
 *      ab. An 338 Aufnahmen vom 26.–28.09. nachgerechnet, mit einer lokalen
 *      Spracherkennung (VAD) als Maß: bei 60 Bildern/s ging in 12 von 676
 *      Läufen die Frist ab, obwohl in den gezählten 3 s über 0,2 s Rede lag,
 *      einmal schon 1 s nach dem letzten Wort. Die jetzige Regel wartete in
 *      diesen Läufen länger. Vorgabe: im Zweifel warten.
 */
export const TON_ANHALTEND_MS = 120;

/**
 * In diesem Fenster kommt der Ton zusammen. (27.09.2026, pre.3374)
 *
 * Gemessen am Satz der Aufnahme 2697 (Sekunde 17–31) mit zugemischtem
 * Fahrgeräusch und an der Pause davor:
 *   - Bei 2 s zählte das erste Rauschstück der Pause (1,8 s nach dem letzten
 *     Wort) mit dem Ende des Satzes zusammen und galt als Ton — die Karte
 *     schickte 1,8 s später ab.
 *   - Bei 1 s wuchs die Stille im Satz bei Fahrgeräusch 0,2 auf 2,3 s (mit
 *     1,5 s: 1,5 s).
 * ⚠️ Die Grenze jeder Regel am Pegel: bei Fahrgeräusch 0,25–0,33 über das
 *    ganze Band zugemischt wächst die Stille im Satz auf 3,1–11,6 s. Dort
 *    liegt die Leistung dieser leisen Stimme 3–6 dB UNTER der des Lärms,
 *    der Pegel im Satz nur 2–13 % über dem Lärm allein, der selbst um
 *    ±30 % schwankt — was diese Sprache als Ton zählte, zählte den Lärm
 *    mit (die Pause des Falls wäre wieder nie Stille). Echtes Fahrgeräusch
 *    liegt aber unter 300 Hz; gemessen wird darum im Sprachband
 *    (SPRACHBAND_AB_HZ). Dort lag der Grund der Fahrt vom 27.09. bei
 *    0,11–0,15 (über das ganze Band 0,22–0,29).
 */
export const TON_FENSTER_MS = 1_500;

/**
 * So viel zählt ein Bild höchstens: der native Takt. Kam es nach einer
 * Lücke (der Bildschirmtakt stockte), zählte sonst die ganze Lücke als Ton.
 */
export const BILD_HOECHSTENS_MS = 100;

/**
 * Kommen länger keine Pegelbilder als das, misst der Pegel nicht — der
 * Rechentakt steht (Ticket 2634), oder die Seite ist verdeckt. (Ticket 2668;
 * seit 28.09.2026 hier statt in ptt-modal.ts, weil auch die Stille sie kennt.)
 *
 * ⚠️ Ticket 2697 (28.09.2026, Fall 16:35): Nach so einer Lücke beginnt die
 *    Stille neu. Verdeckt ruft der Bildschirmtakt gar nicht — und das erste
 *    Bild danach zählte die ganze Lücke als Stille: `[ptt-stille]
 *    laengste=20019ms`, obwohl Martin redete (Seite 16,8 s verdeckt, davor
 *    3 s Pause). Wer nicht misst, weiß nicht, ob gesprochen wurde.
 */
export const PEGEL_LUECKE_MS = 1000;

/**
 * Aus so vielen Abständen ergibt sich die übliche Bilddauer. ⚠️ Eine gerade
 * Zahl: kommen die nativen Antworten im Wechsel spät und früh (45/155 ms),
 * liegen je acht davon im Fenster, und der Median ist 100 ms. Bei einer
 * ungeraden kippte er mit jedem Bild zwischen 45 und 155 ms.
 */
const TAKT_ABSTAENDE = 16;

/**
 * Die übliche Dauer eines Bildes: der Median der letzten Abstände, höchstens
 * BILD_HOECHSTENS_MS; 0, solange es keinen gibt. Ein Stocken des
 * Bildschirmtakts verschiebt ihn kaum; nativ gleichen sich ein verspätetes
 * und das folgende, dafür frühe Bild darin aus (45 + 155 ms → 100 ms).
 */
function bildDauer(abstaende: readonly number[]): number {
  if (abstaende.length === 0) return 0;
  const s = [...abstaende].sort((a, b) => a - b);
  const m = s.length / 2;
  const median = s.length % 2 === 1 ? s[Math.floor(m)] : (s[m - 1] + s[m]) / 2;
  return Math.min(median, BILD_HOECHSTENS_MS);
}

interface Tonlauf {
  /**
   * Ein wirklich gemessenes Bild: sein Pegel (null = zählt nicht, Schonfrist)
   * und die Grenze, die jetzt gilt. Liefert den Beginn des Tons (das
   * früheste gezählte Bild im Fenster), sobald im Fenster TON_ANHALTEND_MS
   * zusammen sind und ein gezähltes Bild dazukam — sonst null.
   */
  bild(jetzt: number, roh: number | null, grenze: number): number | null;
  /** Nicht gemessen: der Ton und der Takt reißen ab. */
  abriss(): null;
  /** Beginn des lauten Stücks, in dem das letzte Bild lag — auch solange es nicht zählt; null = leise. */
  beginn(): number | null;
}

/*
 * ⚠️ Bilder vor dem Grundpegel werden nachgereicht. (27.09.2026, Ticket 2697,
 *    zweite Nachprüfung) Ohne Grundpegel gibt es keine Grenze (stilleGrenze),
 *    also entscheidet über diese Bilder die Grenze, die gilt, solange sie im
 *    Fenster liegen. Wer ab dem ersten Bild spricht, liefert zuerst nur
 *    Sprache — der Grundpegel liegt dann auf der Stimme, und erst die Pause
 *    danach zeigt, dass es Ton war. Jedes spätere Bild zählt mit der Grenze
 *    bei seiner Ankunft: sinkt das Rauschen, wird Vergangenes nicht zu Ton.
 */
function neuerTonlauf(): Tonlauf {
  const abstaende: number[] = [];
  let letztes: number | null = null;
  /** Ein Bild: wann, wie lange es zählt (die übliche Bilddauer zu seiner Zeit), sein Pegel und die Grenze bei Ankunft. */
  type Bild = { t: number; ms: number; roh: number | null; grenze: number };
  /** Die Bilder im Fenster, dazu das eine davor — ob das erste im Fenster zu einem Stück gehört. */
  const bilder: Bild[] = [];
  /** Bis zu welchem gezählten Bild der Ton schon gemeldet ist. */
  let gemeldetBis = Number.NEGATIVE_INFINITY;
  let stueckSeit: number | null = null;
  return {
    bild(jetzt, roh, grenze) {
      if (letztes !== null) {
        abstaende.push(jetzt - letztes);
        if (abstaende.length > TAKT_ABSTAENDE) abstaende.shift();
      }
      letztes = jetzt;
      bilder.push({ t: jetzt, ms: bildDauer(abstaende), roh, grenze });
      while (bilder.length > 1 && jetzt - bilder[1].t >= TON_FENSTER_MS) bilder.shift();
      const laut = (b: Bild): boolean =>
        b.roh !== null && b.roh > (Number.isFinite(b.grenze) ? b.grenze : grenze);
      let ms = 0;
      let erstes: number | null = null;
      let zuletzt = Number.NEGATIVE_INFINITY;
      let stueck: Bild[] = [];
      // Gezählt wird ein Stück erst ab seinem zweiten Bild.
      const schliesse = (): void => {
        if (stueck.length >= 2) {
          for (const b of stueck) {
            if (jetzt - b.t >= TON_FENSTER_MS) continue;
            ms += b.ms;
            erstes ??= b.t;
            zuletzt = b.t;
          }
        }
        stueck = [];
      };
      for (const b of bilder) {
        if (laut(b)) stueck.push(b);
        else schliesse();
      }
      stueckSeit = stueck[0]?.t ?? null;
      schliesse();
      if (ms < TON_ANHALTEND_MS || zuletzt <= gemeldetBis) return null;
      gemeldetBis = zuletzt;
      return erstes;
    },
    abriss() {
      letztes = null;
      bilder.length = 0;
      stueckSeit = null;
      return null;
    },
    beginn: () => stueckSeit,
  };
}

/** Was ein Bild über die Stille sagt (`neuerStillelauf`). */
interface StilleBild {
  /** Beginn des anhaltenden Tons, wenn dieses Bild zu ihm zählt — sonst null. */
  tonSeit: number | null;
  /** Liegt das Bild in einem lauten Stück, auch solange es noch nicht als Ton zählt? */
  laut: boolean;
  /** Wie lange es bis zu diesem Bild still ist (ms). */
  stillMs: number;
}

interface Stillelauf {
  /** Ein Bild: `roh` null = zählt nicht (Schonfrist); `misst` false = nicht gemessen — Ton und Stille reißen ab. */
  bild(jetzt: number, roh: number | null, grenze: number, misst: boolean): StilleBild;
  /** Nach einer Lücke (PEGEL_LUECKE_MS): die Stille beginnt neu, der Ton reißt ab. */
  luecke(): void;
}

/**
 * Die Stille nach EINER Grenze: der Tonlauf und der Beginn der Stille. (07.10.2026)
 * Die Anzeige führt zwei davon — den, der entscheidet, und einen nur gemessenen
 * nach der alten Grenze (ohne Sprachgrenze) für `altMs=` in `[ende-token]`.
 */
export function neuerStillelauf(): Stillelauf {
  const ton = neuerTonlauf();
  /*
   * ⚠️ -1 als „nicht still", NICHT 0. Die Null war gleichzeitig Zeitpunkt
   *    UND Merkzeichen — und `performance.now()` darf legitim 0 sein (beim
   *    allerersten Bild nach dem Laden, und in jeder Pruefung mit fester
   *    Uhr). Dann galt die Stille als nicht begonnen und zaehlte erst beim
   *    naechsten Bild los.
   */
  let seit = -1;
  return {
    bild(jetzt, roh, grenze, misst) {
      /*
       * Tickets 2697/2668 (27.09.2026): die Stille endet erst bei anhaltendem
       * Ton (TON_ANHALTEND_MS in TON_FENSTER_MS), nicht bei jedem Bild über
       * der Grenze. Solange ein lautes Stück läuft, steht die Stille still:
       * sonst könnte sie genau in den ersten Millisekunden eines Satzes die
       * Schwelle erreichen und abschicken. Zählte es nicht, zählt sie von
       * ihrem Beginn weiter, als wäre nichts gewesen.
       */
      const tonSeit = misst ? ton.bild(jetzt, roh, grenze) : ton.abriss();
      // Laut ist dieses Bild, wenn es in einem lauten Stück liegt — EIN Urteil, das des Tonlaufs.
      const laut = ton.beginn() !== null;
      if (!misst || tonSeit !== null) seit = -1;
      else if (seit < 0 && !laut) seit = jetzt;
      return { tonSeit, laut, stillMs: seit < 0 ? 0 : Math.max(0, (ton.beginn() ?? jetzt) - seit) };
    },
    luecke() {
      seit = -1;
      ton.abriss();
    },
  };
}

/**
 * Gemessen wird im Sprachband: ein Hochpass ab dieser Frequenz vor dem
 * Analyser. (27.09.2026, Ticket 2697, zweite Nachprüfung)
 *
 * Fahrgeräusch (Motor, Reifen, Innenraum-Brummen) liegt fast ganz darunter,
 * Sprache darüber. Die neun Aufnahmen der Fahrt vom 27.09., 10:05–10:15,
 * nachgerechnet: über das ganze Band lag die Stimme im Median 6 dB über dem
 * Fahrgeräusch, hinter diesem Hochpass 17 dB. Die Karte von pre.3377 hätte
 * zwei davon mitten im Satz abgeschickt, und eine Pause von 10 s wäre nie
 * Stille geworden; hinter dem Hochpass keine, jede Pause nach 3 s. 33
 * Aufnahmen ohne Fahrgeräusch (26./27.09.): wie ohne Filter.
 * ⚠️ Nur der Browser-Weg. Der native Pegel (averagePower) kommt fertig über
 *    das ganze Band.
 */
export const SPRACHBAND_AB_HZ = 300;

/** Quelle → Hochpass (Sprachband) → Analyser: EINE Kette für den ersten und jeden neu gebauten Kontext. */
function messKette(kontext: AudioContext, quelle: AudioNode, fftSize: number): AnalyserNode {
  const hochpass = kontext.createBiquadFilter();
  hochpass.type = "highpass";
  hochpass.frequency.value = SPRACHBAND_AB_HZ;
  const analyser = kontext.createAnalyser();
  analyser.fftSize = fftSize;
  quelle.connect(hochpass);
  hochpass.connect(analyser);
  return analyser;
}

/**
 * @param onStille Meldet den Wechsel still/nicht-still. Der zweite Wert ist
 *   die gemessene Stille in Millisekunden — wer eine eigene, laengere
 *   Schwelle braucht (Ticket 2420: selbst absenden), rechnet damit weiter,
 *   statt eine zweite Messung danebenzustellen. Der dritte Wert ist der
 *   Beginn des anhaltenden Tons (TON_ANHALTEND_MS), wenn dieses Bild zu
 *   ihm zählt, sonst null. Der vierte Wert ist der
 *   Pegel dieses Bildes — `null`, wenn nicht wirklich gemessen wurde
 *   (Kontext angehalten), und beim Stoppen gar keiner (Ticket 2512).
 */
export function createMicViz(
  bars = 5,
  onStille?: (
    still: boolean,
    stillMs: number,
    tonSeit: number | null,
    roh?: number | null,
    /** Die Grenze, die für dieses Bild galt (Ticket 2606). */
    grenze?: number,
    /**
     * Liegt dieses Bild in einem lauten Stück — auch solange es noch nicht
     * als Ton zählt? Dasselbe Urteil, das hier die Stille anhält. (Ticket 2668)
     */
    laut?: boolean,
    /** 07.10.2026: die Untergrenze aus der Sprache dieser Aufnahme (nur Browser-Weg, sonst fehlt sie) — fürs Journal. */
    sprach?: number,
  ) => void,
  /** Ticket 2634: was die Wache über den Rechentakt meldet (rechentakt.ts). */
  onTakt?: (e: TaktEreignis) => void,
  /**
   * 07.10.2026: gilt die Sprachgrenze für dieses Bild? Die PTT-Karte sagt ja nur im
   * Auto-Modus mit Ja des Servers UND wenn die Live-Mitschrift seit TEXTRUHE_AB_MS steht
   * (ptt-modal.ts). Fehlt das Prädikat, gilt immer die alte Grenze.
   */
  sprachGilt?: () => boolean,
): MicViz {
  let warStill = false;

  /*
   * ⚠️ 20.09.2026 (Ticket 2420) — DIE STILLE-REGEL STAND NUR AN EINER STELLE.
   *
   *    Sie lag mitten in `step()`, also im Weg mit EIGENER Messung (Browser).
   *    Der native Weg fuettert den Pegel von aussen ueber `pushLevel` — und
   *    dort wurde nie Stille gemeldet. Gemessen an Martins sieben Tagen:
   *    117 von 142 PTT-Druecken sind nativ, also 82 % ohne jede
   *    Stille-Erkennung.
   *
   *    Nebenwirkung, die bis heute niemand bemerkt hat: auch die Warnung
   *    „kein Pegel" (Ticket 2230) konnte nativ nie erscheinen.
   *
   *    Jetzt entscheidet EINE Funktion, und beide Wege rufen sie.
   */
  const grundpegel = neuerGrundpegel();
  let stille = neuerStillelauf();
  /** 07.10.2026: dieselbe Stille nach der alten Grenze — nur gemessen (`stilleAltMs`), entscheidet nichts. */
  let stilleAlt = neuerStillelauf();
  let stillAltMs = 0;
  let tonAb = 0;
  let grundAb = 0;
  /*
   * Ticket 2668, Nachbesserung (29.09.2026): was in der Schonfrist laut war,
   * zählt nicht als Sprache — verworfen werden darf es trotzdem nicht. Ein
   * „Ja“ bei 0,7 s ohne rechtzeitige Mitschrift ging sonst mit dem Fenster
   * verloren. Nachgemessen an 213 Fenster-Aufnahmen (Sprache per VAD): die
   * laute Summe in der Schonfrist war ohne Rede bis 680 ms lang, mit Rede ab
   * 140 ms — eine Dauer trennt Ton und Wort also nicht. Darum wird hier nur
   * festgehalten, DASS dort anhaltender Ton lag (dieselbe Regel, eigener
   * Lauf). Gefüttert bis TON_FENSTER_MS nach der Schonfrist: ohne Grundpegel
   * (nativ gibt es ihn erst nach ~1,1 s) urteilt der Lauf nachgereicht.
   */
  let schonlauf = neuerTonlauf();
  let schonTon = false;
  /*
   * 07.10.2026: die Sprache dieser Aufnahme (nur Browser-Weg, `sprachGrenze`).
   * ⚠️ Prüfbefund K2: je Messung NEU (start/stop). Bei „Mikro neu verbunden“
   *    (ptt-modal.ts → viz.start) behielt sie sonst den Pegel des alten Geräts; im
   *    PTT ist autoGainControl aus (ptt.ts) — ein leiseres neues Mikro hätte
   *    Martins Sprache unter die alte Grenze gelegt, sie zählte als Stille.
   */
  let sprachpegel = neuerSprachpegel();
  /** Wann das vorige Bild kam — gemessen oder nicht; null = noch keins. */
  let vorigesBild: number | null = null;
  const merkeStille = (roh: number, misstWirklich: boolean, browser = false): void => {
    const jetzt = performance.now();
    // Ticket 2697 (28.09.2026): nach einer Lücke (PEGEL_LUECKE_MS) beginnen
    // Stille und Ton neu — die Lücke selbst ist nicht gemessen.
    if (vorigesBild !== null && jetzt - vorigesBild > PEGEL_LUECKE_MS) {
      stille.luecke();
      stilleAlt.luecke();
    }
    vorigesBild = jetzt;
    if (misstWirklich && jetzt >= grundAb) grundpegel.merke(jetzt, roh);
    const grenzeAlt = stilleGrenze(grundpegel.wert());
    // 07.10.2026: nur der Browser-Weg, nur gemessene Bilder nach der Schonfrist (der eigene Ton ist keine Sprache).
    // Gelernt wird IMMER; ANGEWANDT nur, wenn der Aufrufer es sagt (`sprachGilt`, Prüfbefund B1).
    if (browser && misstWirklich && jetzt >= tonAb) sprachpegel.merke(roh, grenzeAlt);
    const sprach = browser && sprachGilt?.() === true ? sprachGrenze(sprachpegel.wert()) : 0;
    const grenze = Math.max(grenzeAlt, sprach);
    const zaehlt = jetzt >= tonAb ? roh : null;
    const { tonSeit, laut, stillMs } = stille.bild(jetzt, zaehlt, grenze, misstWirklich);
    // Nur gemessen: was die alte Grenze allein gesagt hätte (`[ende-token] altMs=`).
    stillAltMs = stilleAlt.bild(jetzt, zaehlt, grenzeAlt, misstWirklich).stillMs;
    if (jetzt < tonAb + TON_FENSTER_MS) {
      if (!misstWirklich) schonlauf.abriss();
      else if (schonlauf.bild(jetzt, jetzt < tonAb ? roh : null, grenze) !== null) schonTon = true;
    }
    if (stillMs > STILL_SCHWELLE_MS !== warStill) {
      warStill = stillMs > STILL_SCHWELLE_MS;
    }
    // ⚠️ IMMER melden, nicht nur beim Wechsel. Wer eine laengere Schwelle
    //    hat, braucht den laufenden Wert — beim Wechsel allein stuende er
    //    bei 2500 ms und danach nie wieder.
    // Der dritte Wert: derselbe anhaltende Ton, der hier die Stille beendet,
    // ist dort Sprache. Siehe `gesprochen` in jetzt-senden.ts.
    // Das Journal bekommt die alte Grenze (Wörter „im Stillen“ usw. bleiben wie vorher), dazu die neue Untergrenze.
    onStille?.(warStill, stillMs, tonSeit, misstWirklich ? roh : null, grenzeAlt, laut, sprach > 0 ? sprach : undefined);
  };
  const el = document.createElement("div");
  el.className = "dq-viz";
  el.hidden = true;
  /*
   * 03.10.2026 (pre.3623): ein Bild mit Namen statt versteckt — wer vorliest,
   * erfährt, dass hier der Pegel läuft, und ob er gemessen werden kann. Kein
   * Live-Bereich: der Name ändert sich nur mit der Lage und wird nicht
   * dauernd vorgelesen.
   */
  el.setAttribute("role", "img");
  el.setAttribute("aria-label", LAGE_NAME.misst);
  const barEls: HTMLElement[] = [];
  for (let i = 0; i < bars; i++) {
    const b = document.createElement("span");
    b.className = "dq-viz-bar";
    b.setAttribute("aria-hidden", "true");
    el.appendChild(b);
    barEls.push(b);
  }
  // Steht nur da, wenn kein Signal ankommt oder es nachgereicht kommt (style.css, `[data-lage=…]`).
  const lageText = document.createElement("span");
  lageText.className = "dq-viz-lage";
  lageText.setAttribute("aria-hidden", "true");
  lageText.textContent = LAGE_TEXT_BLIND;
  el.appendChild(lageText);

  /*
   * 03.10.2026 (pre.3623): EIN Zeichner für beide Wege (eigene Messung und
   * von außen gefüttert). Er schreibt nur, was sich sichtbar geändert hat —
   * in Stille also gar nichts. Die Werte rechnet pegel-anzeige.ts.
   */
  let anzeige: PegelAnzeige = neuePegelAnzeige(barEls.length);
  /** 08.10.2026: der Pegel aus der Aufnahme am Server (Nachhören) — je Messung neu (stop). */
  let nachgereichterPegel = neuerNachgereichterPegel();
  const geschrieben = new Array<number>(barEls.length).fill(0);
  let geschriebenJetzt = 0;
  let gezeigteLage: PegelLage | null = null;
  const ruhigeBewegung = (): boolean => {
    try {
      return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    } catch {
      return false;
    }
  };
  /** 18.10.2026: sanfte Anpassung an die Stimme, je Messung neu (nur Anzeige). */
  let anpasse = neueAnpassung();
  const neueAnzeige = (): void => {
    anpasse = neueAnpassung();
    anzeige = neuePegelAnzeige(barEls.length, ruhigeBewegung());
  };
  const zeigeLage = (lage: PegelLage | null): void => {
    if (lage === gezeigteLage) return;
    gezeigteLage = lage;
    if (lage === null) delete el.dataset.lage;
    else el.dataset.lage = lage;
    el.setAttribute("aria-label", LAGE_NAME[lage ?? "misst"]);
    // Nur beim Wechsel der Lage geschrieben, nie je Bild.
    if (lage !== null) lageText.textContent = LAGE_TEXT[lage];
  };
  const schreibe = (werte: readonly number[], jetzt: number): void => {
    for (let i = 0; i < barEls.length; i++) {
      const v = werte[i] ?? 0;
      if (Math.abs(v - geschrieben[i]) < ZEICHNE_AB && !(v === 0 && geschrieben[i] !== 0)) continue;
      geschrieben[i] = v;
      barEls[i].style.setProperty("--pegel", v.toFixed(3));
    }
    if (Math.abs(jetzt - geschriebenJetzt) >= 2 * ZEICHNE_AB || (jetzt === 0 && geschriebenJetzt !== 0)) {
      geschriebenJetzt = jetzt;
      el.style.setProperty("--pegel-jetzt", jetzt.toFixed(2));
    }
  };
  const male = (jetzt: number): void => {
    const b = anzeige.bild(jetzt);
    zeigeLage(b.lage);
    schreibe(b.werte, b.jetzt);
  };
  const ruheAnzeige = (): void => {
    neueAnzeige();
    zeigeLage(null);
    schreibe(new Array<number>(barEls.length).fill(0), 0);
  };

  let ac: AudioContext | null = null;
  let src: MediaStreamAudioSourceNode | null = null;
  let raf = 0;
  let letzterWeckversuch = 0;
  /*
   * Ticket 2634 (26.09.2026): die Wache über den Rechentakt, und was eine
   * Rettung zurücklässt. ⚠️ Den hängenden Kontext NICHT mitten in der
   * Aufnahme schließen: seine Quelle liest die Spur der Aufnahme selbst, und
   * das Schließen hat die Aufnahme-Kette früher mitgerissen (siehe
   * starteStromWache in ptt.ts). Er wird beim Stoppen geschlossen, die Kopie
   * der Spur für den neuen Kontext dort gestoppt — eine geklonte Spur hält
   * sonst das Mikro offen.
   */
  let taktWache: TaktWache | null = null;
  const abgeloesteKontexte: AudioContext[] = [];
  const spurKopien: MediaStreamTrack[] = [];

  /** Weckt den Kontext, hoechstens alle 500 ms — resume() ist nicht gratis. */
  const wecke = (): void => {
    if (!ac || ac.state === "running") return;
    const jetzt = Date.now();
    if (jetzt - letzterWeckversuch < 500) return;
    letzterWeckversuch = jetzt;
    void ac.resume().catch(() => undefined);
  };

  /** Legt den Kontext an, falls es noch keinen gibt, und weckt ihn sofort. */
  const holeKontext = (): AudioContext | null => {
    if (ac) {
      wecke();
      return ac;
    }
    // Rueckfall wie in der PTT-Karte — aeltere WebKit-Staende kennen nur
    // die praefixierte Fassung.
    const AC =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    try {
      ac = new AC();
    } catch {
      return null;
    }
    wecke();
    return ac;
  };

  const prepare = (): void => {
    holeKontext();
  };

  const stop = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    // ⚠️ Zuruecksetzen, sonst meldet der naechste Lauf sofort „still".
    // Der naechste Lauf kann den anderen Weg nehmen (nativ/Browser) — mit
    // eigenem Takt.
    stille = neuerStillelauf();
    stilleAlt = neuerStillelauf();
    stillAltMs = 0;
    vorigesBild = null;
    sprachpegel = neuerSprachpegel();
    if (warStill) {
      warStill = false;
    }
    // Beim Stoppen auf null — sonst haelt ein Aufrufer mit eigener Schwelle
    // den letzten Stand fest und sendet nach dem Aufhoeren.
    onStille?.(false, 0, null);
    taktWache?.stop();
    taktWache = null;
    try {
      src?.disconnect();
    } catch {
      /* schon getrennt */
    }
    src = null;
    for (const k of [ac, ...abgeloesteKontexte.splice(0)]) {
      try {
        void k?.close();
      } catch {
        /* schon zu */
      }
    }
    ac = null;
    for (const t of spurKopien.splice(0)) {
      try {
        t.stop();
      } catch {
        /* schon gestoppt */
      }
    }
    if (fremdRaf) cancelAnimationFrame(fremdRaf);
    fremdRaf = 0;
    nachgereichterPegel = neuerNachgereichterPegel();
    el.hidden = true;
    ruheAnzeige();
  };

  const start = (stream: MediaStream | MediaStreamTrack): void => {
    // pre.3623: ein Besitzer — eine von außen gefütterte Zeichenschleife endet hier.
    if (fremdRaf) cancelAnimationFrame(fremdRaf);
    fremdRaf = 0;
    // ⚠️ NICHT stop() — das schloss bisher den Kontext. Genau der ist aber
    // das Wertvolle: in der Fingergeste angelegt und geweckt. Ihn hier
    // wegzuwerfen und einen neuen zu bauen hiesse, die Geste zu verlieren.
    // Nur die laufende Messung wird abgeraeumt.
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    // 07.10.2026 (Prüfbefund K2): ein neues Gerät hat seinen eigenen Pegel — die Sprache lernt neu.
    sprachpegel = neuerSprachpegel();
    // 08.10.2026: auch der nachgereichte Pegel gehört zu EINER Messung — eine späte Probe davor spielt hier nicht.
    nachgereichterPegel = neuerNachgereichterPegel();
    taktWache?.stop();
    taktWache = null;
    try {
      src?.disconnect();
    } catch {
      /* schon getrennt */
    }
    src = null;
    try {
      el.hidden = false;
      // Eine einzelne Tonspur (PTT-Karte) in einen Strom wickeln.
      const ms =
        stream instanceof MediaStream ? stream : new MediaStream([stream]);
      // Der warmgehaltene Strom schaltet seine Spur stumm (enabled=false).
      // Wird er uebernommen, ohne wieder aktiviert zu werden, liefert auch ein
      // laufender Kontext keinen Pegel. Hier defensiv sicherstellen.
      for (const t of ms.getAudioTracks()) {
        if (!t.enabled) t.enabled = true;
      }
      // Rueckfall wie in der PTT-Karte — aeltere WebKit-Staende kennen nur
      // die praefixierte Fassung.
      // 2026-07-25 (Ticket 718.3, Martins Bild): Auf iOS startet ein neuer
      // AudioContext ANGEHALTEN. Ohne Wecken liefert der Analyser nur Nullen —
      // die Balken standen still, obwohl gesprochen wurde.
      // 2026-08-01 (Ticket 1536): Deshalb wird er moeglichst schon in der
      // Geste angelegt (prepare()); hier wird er nur noch geholt.
      const kontext = holeKontext();
      if (!kontext) return;
      src = kontext.createMediaStreamSource(ms);
      // 2026-07-25 (Ticket 562): fftSize dient jetzt nur noch dem Zeitfenster,
      // nicht mehr der Frequenz-Aufteilung. Ticket 2697: im Sprachband.
      let analyser = messKette(kontext, src, 1024);
      const buf = new Float32Array(analyser.fftSize);
      /*
       * Ticket 2634, Stufe 2 der Wache: ein frischer Kontext auf einer KOPIE
       * der Spur. Der alte bleibt offen bis stop() (siehe oben).
       */
      const baueNeu = (): AudioContext | null => {
        const spur = ms.getAudioTracks()[0];
        if (!spur || typeof spur.clone !== "function") return null;
        try {
          src?.disconnect();
        } catch {
          /* schon getrennt */
        }
        src = null;
        if (ac) abgeloesteKontexte.push(ac);
        ac = null;
        const neu = holeKontext();
        if (!neu) return null;
        const kopie = spur.clone();
        spurKopien.push(kopie);
        src = neu.createMediaStreamSource(new MediaStream([kopie]));
        analyser = messKette(neu, src, buf.length);
        return neu;
      };
      taktWache = wacheRechentakt(kontext, {
        neuBauen: baueNeu,
        melde: (e) => onTakt?.(e),
      });
      // Der Verlauf (die Welle) steht seit pre.3623 in pegel-anzeige.ts.
      neueAnzeige();
      const step = (): void => {
        if (!ac) return;
        // Kam der Kontext doch angehalten zur Welt (Geste verfallen, iOS
        // zickt), hier weiter versuchen statt still Nullen zu zeichnen.
        // Ohne das blieb der Visualizer fuer die ganze Aufnahme tot.
        if (ac.state !== "running") wecke();
        analyser.getFloatTimeDomainData(buf);
        // Effektivwert = tatsaechliche Lautstaerke im Zeitbereich.
        let summe = 0;
        for (let i = 0; i < buf.length; i++) summe += buf[i] * buf[i];
        // 2026-07-25 (Martin: "der Ausschlag ist so gering"): Sprache liegt
        // im Effektivwert bei etwa 0,01-0,08. Eine reine Multiplikation liess
        // das am unteren Rand zappeln. Die Wurzel-Kennlinie hebt leise
        // Anteile kraeftig an und haelt laute im Rahmen.
        // pre.3623: ungekappt für die Anzeige (weicher Anschlag statt Deckel,
        // anzeigeWert), gekappt wie immer für die Stille-Entscheidung.
        const kennlinie = kennlinieAus(Math.sqrt(summe / buf.length));
        const roh = Math.min(1, kennlinie);
        /*
         * ⚠️ 2026-08-29 — Stille MELDEN, nicht nur zeichnen.
         *
         * Martin: *„Der Visualizer geht auch nicht. Außerdem sollte er
         * stärker ausschlagen, weil er ganz wenig ausschlägt und es
         * verwirrt, weil man nicht weiß, ob überhaupt irgendwas
         * funktioniert."*
         *
         * Auf seinem Foto standen alle Balken auf dem Ruhewert — es kam
         * kein Signal an. Eine flache Linie sieht aber aus wie eine
         * arbeitende Anzeige. Wer sie sieht, weiss nicht, ob das Mikro
         * still ist oder die Anzeige tot.
         *
         * Deshalb zaehlt der Verlauf jetzt mit, wie lange NICHTS kommt,
         * und sagt es dem Aufrufer. Die Aufnahme laeuft dabei weiter —
         * der Server erkennt hinterher ohnehin.
         */
        // 05.09.2026 (#2091, „Kein Ton" bei laufender Aufnahme): Ein
        // angehaltener Kontext liefert Nullen — das ist kein stilles Mikro,
        // sondern ein schlafender Messer. iOS hält ihn genau dann an, wenn
        // die native Tonsitzung umgebaut wird (Tagesaufnahme pausiert,
        // Mikro wechselt) — also beim PTT-Start. Stille zählt nur, solange
        // der Kontext wirklich läuft.
        // Ticket 2634: steht der Rechentakt, ist die Null keine Messung —
        // auch wenn der Kontext „running" sagt.
        const misst = ac.state === "running" && !taktWache?.steht();
        merkeStille(roh, misst, true);
        /*
         * pre.3623: Für die ANZEIGE trägt ein Bild nur Signal, wenn es
         * gemessen ist UND nicht exakt digitale Null — die liefert kein
         * echtes Mikrofon, wohl aber ein Kontext, dessen Takt steht, bevor
         * die Wache es nach 1,2 s weiß (Journal 28.09.–03.10.: 67 von 82
         * flachen Aufnahmen Martins mit `takt=steht`), und eine stumme
         * Quelle. Die Stille-Entscheidung oben bleibt, wie sie war.
         */
        const jetzt = performance.now();
        const eigen = misst && summe > 0 ? anpasse(performance.now(), anzeigeWertAusQuadratmittel(summe / buf.length)) : null;
        /*
         * 08.10.2026: Woher der Pegel kommt, entscheidet NUR diese Zeile. Die eigene
         * Messung geht vor; liefert sie nichts, der Pegel aus der Aufnahme am Server
         * (nachgereicht, pegel-anzeige.ts). Der Vorrat läuft nach der Uhr ab — gilt die
         * eigene Messung, verfällt er ungespielt.
         * Prüfbefund K2: digitale Stille (0) zählt wie bei der eigenen Messung
         * (`summe > 0`) nicht als Signal — sonst stünde „Pegel verzögert“ über ruhenden
         * Punkten, wo nichts ankommt.
         */
        const ersatzRoh = nachgereichterPegel.wert(jetzt);
        const ersatz = ersatzRoh !== null && ersatzRoh > 0 ? anpasse(jetzt, ersatzRoh) : null;
        anzeige.messe(jetzt, eigen ?? ersatz, eigen === null && ersatz !== null);
        male(jetzt);
        raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    } catch {
      /* Visualizer ist Kuer — Aufnahme laeuft auch ohne */
    }
  };

  /*
   * 2026-07-25: Anzeige ohne eigene Messung — der Pegel wird von aussen
   * gefuettert (siehe pushLevel). Kein AudioContext, keine Konkurrenz zur
   * Quelle.
   *
   * pre.3623: Gezeichnet wird trotzdem je Bild (`fremdRaf`), nicht je
   * Messwert. Der native Pegel kommt alle 100 ms; bisher sprang jeder Balken
   * zehnmal je Sekunde, und bleiben die Werte aus, merkt es nur ein Takt, der
   * weiterläuft — dann sagt die Anzeige „blind“ (BLIND_AB_MS).
   * ⚠️ Jeder gefütterte Wert zählt als Signal, auch 0: der native Weg meldet
   *    unter −50 dB als 0, und ein stiller Raum ist kein Ausfall.
   */
  let fremdRaf = 0;
  const fremdBild = (): void => {
    male(performance.now());
    fremdRaf = requestAnimationFrame(fremdBild);
  };
  const starteFremdBild = (): void => {
    if (fremdRaf) return;
    el.hidden = false;
    fremdRaf = requestAnimationFrame(fremdBild);
  };

  const zeigeOhneEigeneMessung = (): void => {
    stop();
    el.hidden = false;
    starteFremdBild();
  };

  const pushLevel = (level: number | null): void => {
    const roh = Math.max(0, Math.min(1, level ?? 0));
    // ⚠️ Der native Weg zaehlt seine Stille ab jetzt genauso mit.
    merkeStille(roh, true);
    if (raf) return; // Die eigene Messung zeichnet schon — EIN Besitzer.
    const jetztNativ = performance.now();
    anzeige.messe(jetztNativ, level === null ? null : anpasse(jetztNativ, anzeigeWertNativ(roh)));
    starteFremdBild();
  };

  const nachgereicht = (db: readonly number[]): void => {
    // Geprüft ist die Antwort schon (nachhoeren.ts, `nachhoerPegel`); der Vorrat begrenzt sich selbst.
    nachgereichterPegel.dazu(performance.now(), db.map(anzeigeWertAusDb));
  };

  const tonZaehltAb = (t: number): void => {
    tonAb = t;
    schonlauf = neuerTonlauf();
    schonTon = false;
  };
  const grundLerntAb = (t: number): void => {
    grundAb = t;
  };

  return {
    el,
    prepare,
    start,
    stop,
    zeigeOhneEigeneMessung,
    pushLevel,
    nachgereicht,
    tonZaehltAb,
    grundLerntAb,
    tonInSchonfrist: () => schonTon,
    stilleAltMs: () => stillAltMs,
  };
}
